import type { AxiosResponse } from 'axios';

import { API_KEY, APP_ID, LOGIN_EXTRA_INFO } from 'src/constants';
import { api, api2 } from 'src/utils/axios';
import toClientPassword from 'src/lib/toClientPassword';
import type SocialAccountProvider from 'src/models/SocialAccountProvider';
import type {
  AuthLinkResponse,
  AuthResponse,
  ForgotPasswordRequest,
  LogAuthEventRequest,
  LoginRequest,
  RefreshTokenRequest,
  ResetPasswordRequest,
  SignUpParams,
  SignUpRequest,
  SocialCodeRequest,
  SocialSignUpParams,
  SocialSignUpRequest,
  ValidateResetCodeRequest,
  ValidateResetCodeResponse,
} from './types';

const URL = {
  login: '/login',
  signUp: '/user',
  refreshAccessToken: '/refreshAccessToken',
  forgotPassword: '/forgotPassword',
  validateResetCode: '/validateResetCode',
  resetPassword: '/resetPassword',
  logAuthEvent: '/app/logAuthEvent',
};

class AuthApi {
  login(
    email: string,
    hashedPassword: string,
  ): Promise<AxiosResponse<AuthResponse>> {
    const payload: LoginRequest = {
      email,
      password: hashedPassword,
      ...LOGIN_EXTRA_INFO,
      app_id: APP_ID,
      api_key: API_KEY,
    };
    return api.post(URL.login, payload);
  }

  requestResetPassword(email: string): Promise<AxiosResponse<unknown>> {
    const payload: ForgotPasswordRequest = {
      email,
    };
    return api.post(URL.forgotPassword, payload);
  }

  validateResetCode(
    email: string,
    code: string,
  ): Promise<AxiosResponse<ValidateResetCodeResponse>> {
    const payload: ValidateResetCodeRequest = {
      email,
      code,
    };

    return api.post(URL.validateResetCode, payload);
  }

  resetPassword(
    email: string,
    update_token: string,
    password: string,
  ): Promise<AxiosResponse<AuthResponse>> {
    const payload: ResetPasswordRequest = {
      update_token,
      email,
      password: toClientPassword(password),
      ...LOGIN_EXTRA_INFO,
      app_id: APP_ID,
      api_key: API_KEY,
    };

    return api.post(URL.resetPassword, payload);
  }

  signUp({
    email,
    hashedPassword: password,
    appLanguage: app_language,
    name,
    secondary_name,
    secondary_email,
    secondary_phone,
    birthdate,
    country,
    region_code,
    parent_email,
    primaryContact: primary_contact_type,
    sourceTracking,
    opt_out_audio_recognition,
    opt_out_audio_training,
    accepted_tos,
    accepted_privacy_policy,
    marketing_consent,
    support_person_consent,
    cookies_consent_types,
  }: SignUpParams): Promise<AxiosResponse<AuthResponse>> {
    const payload: SignUpRequest = {
      email,
      password,
      app_language,
      name,
      secondary_name,
      secondary_email,
      secondary_phone,
      birthdate,
      country,
      region_code,
      parent_email,
      primary_contact_type,
      ...(sourceTracking && { source_tracking: sourceTracking }),
      ...LOGIN_EXTRA_INFO,
      opt_out_audio_recognition,
      opt_out_audio_training,
      accepted_tos,
      accepted_privacy_policy,
      marketing_consent,
      support_person_consent,
      cookies_consent_types,
      app_id: APP_ID,
      api_key: API_KEY,
    };
    return api.post(URL.signUp, payload);
  }

  refreshToken(refreshToken: string): Promise<AxiosResponse<AuthResponse>> {
    const payload: RefreshTokenRequest = {
      refreshToken,
      ...LOGIN_EXTRA_INFO,
    };
    return api.post(URL.refreshAccessToken, payload);
  }

  logAuthEvent(event: string): Promise<AxiosResponse<unknown>> {
    const payload: LogAuthEventRequest = { event };
    return api.post(URL.logAuthEvent, payload);
  }

  logout(): Promise<AxiosResponse<unknown>> {
    return this.logAuthEvent('logout');
  }

  isRefreshTokenUrl(url: string): boolean {
    return URL.refreshAccessToken === url;
  }

  isAuthUrl(url: string): boolean {
    return Object.values(URL).includes(url);
  }

  // Social auth (google, facebook, clever, ...) — one implementation for every
  // provider; only the `/app/auth/<provider>/...` path segment differs.
  getSocialAuthLink(
    provider: SocialAccountProvider,
    state: unknown,
    redirect_uri: string,
    extraParams: Record<string, string> = {},
  ): Promise<AxiosResponse<AuthLinkResponse>> {
    return api2.get(`/app/auth/${provider}/auth_url`, {
      params: {
        state,
        redirect_uri,
        ...extraParams,
      },
    });
  }

  signUpWithSocialCode(
    provider: SocialAccountProvider,
    {
      code,
      redirect_uri,
      secondary_name,
      secondary_email,
      secondary_phone,
      birthdate,
      country,
      region_code,
      parentEmail,
      sourceTracking,
      opt_out_audio_recognition,
      opt_out_audio_training,
      accepted_tos,
      accepted_privacy_policy,
      marketing_consent,
      support_person_consent,
      cookies_consent_types,
    }: SocialSignUpParams,
  ): Promise<AxiosResponse<AuthResponse>> {
    const payload: SocialSignUpRequest = {
      code,
      redirect_uri,
      app_id: APP_ID,
      api_key: API_KEY,
      secondary_name,
      secondary_email,
      secondary_phone,
      birthdate,
      country,
      region_code,
      parentEmail,
      ...(sourceTracking && { source_tracking: sourceTracking }),
      opt_out_audio_recognition,
      opt_out_audio_training,
      accepted_tos,
      accepted_privacy_policy,
      marketing_consent,
      support_person_consent,
      cookies_consent_types,
    };
    return api2.post(`/app/auth/${provider}/code/sign_up`, payload);
  }

  signInWithSocialCode(
    provider: SocialAccountProvider,
    code: string,
    redirect_uri: string,
  ): Promise<AxiosResponse<AuthResponse>> {
    const payload: SocialCodeRequest = {
      code,
      redirect_uri,
      app_id: APP_ID,
      api_key: API_KEY,
    };
    return api2.post(`/app/auth/${provider}/code/sign_in`, payload);
  }

  linkWithSocialCode(
    provider: SocialAccountProvider,
    code: string,
    redirect_uri: string,
  ): Promise<AxiosResponse<unknown>> {
    const payload: SocialCodeRequest = {
      code,
      redirect_uri,
      app_id: APP_ID,
      api_key: API_KEY,
    };
    return api2.post(`/app/auth/${provider}/code/link`, payload);
  }

  unlinkSocial(
    provider: SocialAccountProvider,
  ): Promise<AxiosResponse<unknown>> {
    return api2.post(`/app/auth/${provider}/code/unlink`);
  }
}

export const authApi = new AuthApi();
